import type { AxiosResponse } from 'axios'
import { RtnCode } from '@library/enums'
import type { ApiResponse } from './types'
import { parseXmlResponse } from './parse-xml-response'
import type { X2jOptions } from 'fast-xml-parser'

/**
 * 將 XML fetch 回應包裝成 ApiResponse 格式，以便使用 apiValidator 進行驗證
 *
 * @param response - fetch Response 物件
 * @param options - XMLParser 選項（可選）
 * @returns 包裝後的 ApiResponse
 *
 * @example
 * ```typescript
 * const response = await fetch('https://api.example.com/data.xml')
 * return wrapXmlResponse<MyDataType>(response)
 * ```
 */
export async function wrapXmlResponse<T>(
  response: Response,
  options?: Partial<X2jOptions>
): Promise<ApiResponse<T>> {
  const data = await parseXmlResponse<T>(response, options)

  const baseResponse = {
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(
      response.headers as unknown as Iterable<[string, string]>
    ),
    config: { headers: {} } as AxiosResponse['config'],
    request: undefined
  }

  if (response.ok) {
    return {
      ...baseResponse,
      data: {
        rtnCode: RtnCode.CODE_SUCCESS,
        message: 'Success',
        pagination: null,
        sort: null,
        data
      }
    }
  }

  return {
    ...baseResponse,
    data: {
      rtnCode: RtnCode.EVA_API_FAILURE,
      message: response.statusText,
      pagination: null,
      sort: null,
      data: null
    }
  }
}
